import { useContext, useEffect, useCallback, useState } from 'preact/hooks';
import { createContext, type ComponentChildren } from 'preact';
import { Signal, useComputed, useSignal } from '@preact/signals';
import useRoute from './useRoute.tsx';
import {
  getFavoriteProducts,
  setFavoriteProducts
} from '../services/CartService.ts';
import useWidgetOptions from './useWidgetOptions.tsx';
import {
  alternativesItemListName,
  type Options
} from '../ProductList/config/index.ts';
import { getProductsByIds } from '../services/ProductService.ts';
import { type Product, type ProductType } from '@/domain/Product.ts';
import { getProducts } from '@/widgets/services/ProductService.ts';
import { trackProductIsFavoriteToggledFrom } from '../Favorites/Tracking.ts';
import useMarketConfig from './useMarketConfig.tsx';
import { notify } from '../components/controls/Notifications/Notify/Notify.tsx';
import useTranslations from './useTranslations.tsx';
import type { Translations } from '@/domain/WidgetConfig.ts';

type Favorites = {
  favoriteProducts: Signal<Set<string> | undefined>;
  toggleFavorite: (product: Product, itemListName: string) => Promise<boolean>;
  fetchFavoriteProductsData: () => Promise<
    Awaited<ReturnType<typeof getProductsByIds>>
  >;
  loadFavoriteProducts: () => Promise<void>;
  favoriteProductsWidget: Product[];
  loading: boolean;
  optimisticRemovalOfFav: (productId: string) => void;
  optimisticAddFav: (product: Product) => void;
};

const FavoritesContext = createContext<Favorites | null>(null);

export function FavoritesProvider({
  children
}: {
  children: ComponentChildren;
}) {
  const { t } = useTranslations<Translations>();
  const favoriteProducts = useSignal<Set<string>>();
  const { discoveryOfferBundleProducts, financialSettings } = useMarketConfig();
  const { market, channel, locale, inPLP, inPDP, inFavorites } = useRoute();
  const { displayFavoriteButton } = useWidgetOptions<Options>();
  const [favoriteProductsWidget, setFavoriteProductsWidget] = useState<
    Product[]
  >([]);
  const [loading, setLoading] = useState(true);

  async function loadFavoriteProducts() {
    setLoading(true);

    try {
      const favoriteProductsFav = await fetchFavoriteProductsData();
      setFavoriteProductsWidget(favoriteProductsFav);
    } catch (error) {
      console.error('Error loading favorite products:', error);
    } finally {
      setLoading(false);
    }
  }

  function optimisticRemovalOfFav(productId: string) {
    setFavoriteProductsWidget((prev) =>
      prev.filter((item) => item.id !== productId)
    );
  }

  function optimisticAddFav(product: Product) {
    setFavoriteProductsWidget((prev) => [product, ...prev]);
  }

  async function loadFavorites() {
    try {
      const res = await getFavoriteProducts(market, channel);
      favoriteProducts.value = res.favoriteProducts;
    } catch (e) {
      /* No logged user or ECAPI error */
    }
  }

  function toggleFavoriteHelper(favoritesSet: Set<string>, productId: string) {
    const wasFavoriteToggled = favoritesSet.has(productId);
    const updatedFavorites = new Set(favoritesSet);

    if (wasFavoriteToggled) {
      updatedFavorites.delete(productId);
    } else {
      updatedFavorites.add(productId);
    }
    return updatedFavorites;
  }

  async function toggleFavorite(
    product: Product,
    itemListName: string
  ): Promise<boolean> {
    const favorites = favoriteProducts.peek();
    // TODO: I know that this is not part of this PR but counting the favorites before and after is not a very reliable method.
    // If the favorites have changed on the backend we don't know if the number has increased or not.
    // We know the intention of the user, to add or remove, based on if the favorite is in the current list,
    // so that's what I would use to detemine if we are adding or not, rather then counting
    // based on https://dsu-bitbucket.nestle.biz/projects/NNHQE/repos/nespresso-site-partials/pull-requests/960/overview?commentId=828468
    const nInitialFavorites = favorites ? favorites.size : 0;
    const productId = product.id;

    if (!favorites) return true;

    const optimisticFavorites = toggleFavoriteHelper(favorites, productId);

    const nFinalFavorites = optimisticFavorites ? optimisticFavorites.size : 0;
    favoriteProducts.value = optimisticFavorites;

    let notRolledBack = true;

    const res = await getFavoriteProducts(market, channel);

    const newFavorites = toggleFavoriteHelper(res.favoriteProducts, productId);

    await setFavoriteProducts(market, channel, newFavorites)
      .then((response) => {
        favoriteProducts.value = response.favoriteProducts;
      })
      .catch(() => {
        // Rollback optimistic update on error
        favoriteProducts.value = new Set(favorites);

        console.warn(
          `Failed to update favorites for product ${product.id}. Rolling back optimistic update.`
        );

        notRolledBack = false;
      });

    const favoriteAdded = nFinalFavorites > nInitialFavorites;

    if (notRolledBack) {
      sendTracking(itemListName, product, favoriteAdded, nFinalFavorites);
      if (inFavorites) {
        if (favoriteAdded) {
          optimisticAddFav(product);
        } else {
          optimisticRemovalOfFav(product.id);
        }
      }
    } else {
      showNotificationError(inFavorites, inPDP, inPLP, favoriteAdded);
    }

    return notRolledBack;
  }

  function sendTracking(
    itemListName: string,
    product: Product,
    favoriteAdded: boolean,
    nFinalFavorites: number
  ) {
    const currencyCode = financialSettings.defaultCurrency;

    if (inPLP) {
      if (itemListName === alternativesItemListName) {
        trackProductIsFavoriteToggledFrom(
          discoveryOfferBundleProducts,
          itemListName,
          favoriteAdded,
          currencyCode,
          product,
          nFinalFavorites,
          'plp-alternatives'
        );
      } else {
        trackProductIsFavoriteToggledFrom(
          discoveryOfferBundleProducts,
          itemListName,
          favoriteAdded,
          currencyCode,
          product,
          nFinalFavorites,
          'plp'
        );
      }
    }

    if (inPDP) {
      trackProductIsFavoriteToggledFrom(
        discoveryOfferBundleProducts,
        itemListName,
        favoriteAdded,
        currencyCode,
        product,
        nFinalFavorites,
        'pdp'
      );
    }
  }

  function showNotificationError(
    inFavoritesParam: boolean,
    inPDPParam: boolean,
    inPLPParam: boolean,
    favoriteAddedParam: boolean
  ) {
    if (inFavoritesParam) {
      notify('error', t('favorites.errors.removingFavorite'));
    }

    if (inPLPParam) {
      if (favoriteAddedParam) {
        notify('error', t('favorites.errors.addingFavorite'));
      } else {
        notify('error', t('favorites.errors.removingFavorite'));
      }
    }

    if (inPDPParam) {
      if (favoriteAddedParam) {
        notify('error', t('favorites.errors.addingFavorite'));
      } else {
        notify('error', t('favorites.errors.removingFavorite'));
      }
    }
  }

  async function fetchFavoriteProductsData() {
    const ids = favoriteProducts.peek();
    if (!ids) return [];

    // the API returns the data last added - last shown, but we need to reverse it so we show last added first
    const reversedIds = new Set([...ids].reverse());

    const productTypes: ProductType[] = ['capsule', 'accessory', 'machine'];

    const allProductsApi = await Promise.all(
      productTypes.map((productType) =>
        getProducts(
          market,
          channel,
          productType,
          locale,
          undefined,
          undefined,
          false
        )
      )
    );

    const allProductsApiFlat = allProductsApi.flat();

    const favProducts = Array.from(reversedIds)
      .map((id) => allProductsApiFlat.find((product) => product.id === id))
      .filter((product): product is Product => product !== undefined);

    if (!favProducts) {
      return [];
    }

    return favProducts;
  }

  useEffect(() => {
    if (displayFavoriteButton) {
      loadFavorites();
    }
  }, [displayFavoriteButton]);

  return (
    <FavoritesContext.Provider
      value={
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        {
          favoriteProducts,
          toggleFavorite,
          fetchFavoriteProductsData,
          loadFavoriteProducts,
          favoriteProductsWidget,
          loading,
          optimisticRemovalOfFav,
          optimisticAddFav
        }
      }
    >
      {children}
    </FavoritesContext.Provider>
  );
}

/**
 * The `useFavorites` hook provides functionality to manage favorite products.
 * It allows toggling favorite status, fetching favorite products data, and
 * loading favorite products. The hook can be used in contexts where a product
 * is available or in places where no specific product is provided (e.g., FavoritesLayout).
 *
 * @param {string} itemListName - The name of the item list for tracking purposes.
 * @param {Product} [product] - The product to check or toggle favorite status. Optional.
 *
 * @returns {Object} An object containing:
 * - `isFavoriteFeatureEnabled` (Signal<boolean>): Whether the favorite feature is enabled.
 * - `isFavorite` (Signal<boolean>): Whether the product is marked as favorite.
 * - `toggleFavorite` (Function): A function to toggle the favorite status of the product.
 * - `fetchFavoriteProductsData` (Function): A function to fetch data for favorite products.
 * - `loadFavoriteProducts` (Function): A function to load favorite products.
 * - `favoriteProducts` (Product[]): The list of favorite products.
 * - `favoriteWidgetIsLoading` (boolean): Whether the favorite widget is loading.
 * - `optimisticRemovalOfFav` (Function): A function to optimistically remove a product from favorites.
 */
export default function useFavorites(
  itemListName: string,
  product?: Product
): {
  isFavoriteFeatureEnabled: Signal<boolean>;
  isFavorite: Signal<boolean>;
  toggleFavorite: () => Promise<boolean>;
  fetchFavoriteProductsData: () => Promise<Product[]>;
  loadFavoriteProducts: () => Promise<void>;
  favoriteProducts: Product[];
  favoriteWidgetIsLoading: boolean;
  optimisticRemovalOfFav: (productId: string) => void;
  optimisticAddFav: (product: Product) => void;
} {
  const favs = useContext(FavoritesContext);
  const productId = product?.id;

  if (!favs) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }

  const isFavoriteFeatureEnabled = useComputed(() =>
    Boolean(favs.favoriteProducts.value)
  );

  const isFavorite = useComputed(() =>
    Boolean(productId && favs.favoriteProducts.value?.has(productId))
  );

  const toggleFavorite = useCallback(async () => {
    if (!product) return false;

    return favs.toggleFavorite(product, itemListName);
  }, [product, itemListName, favs]);

  return {
    isFavoriteFeatureEnabled,
    isFavorite,
    toggleFavorite,
    fetchFavoriteProductsData: favs.fetchFavoriteProductsData,
    loadFavoriteProducts: favs.loadFavoriteProducts,
    favoriteProducts: favs.favoriteProductsWidget,
    favoriteWidgetIsLoading: favs.loading,
    optimisticRemovalOfFav: favs.optimisticRemovalOfFav,
    optimisticAddFav: favs.optimisticAddFav
  };
}
